/*
Author: Eli Elad Elrom
Website: https://EliElrom.com
License: MIT License
Component: src/component/BubbleChart/BubbleChart.tsx
*/

import React, { useCallback, useEffect, useState } from 'react';
import { Types } from './types';
import * as d3 from 'd3';
import BarChartHelper from './BarChartHelper';
import { randomFillColor, randomFillColorGrey } from '../../../utils/randomColors';

const BubbleChart = (props: IBubbleChartProps) => {
  const [loaded, setLoaded] = useState(false);
  const [prevHeight, setPrevHeight] = useState(props.dimensions.height);
  const [prevWidth, setPrevWidth] = useState(props.dimensions.width);

  const [, setForceData] = useState<Types.ForceData[]>([]);

  const memoizedDrawCallback = useCallback(() => {
    d3.select('#word-cloud-chart-group').selectAll('*').remove();
  }, [props.data, props.dimensions]);

  const memoizedUpdateCallback = useCallback(() => {
    setForceData(props.data);

    const scales = BarChartHelper.getScales(props.data);

    // Compute layout: create a 1-deep hierarchy, and pack it.
    const root = d3
      .pack()
      .size([props.dimensions.boundedWidth, props.dimensions.boundedHeight])
      .padding(props.wordsPadding)(
      d3
        .hierarchy({ children: scales.unique })
        // @ts-ignore
        .sum((i) => scales.size[i])
    );

    const svg = d3
      .select('#word-cloud-chart-group')
      .append('svg')
      .attr('style', 'max-width: 100%; height: auto; height: intrinsic;')
      .attr('font-size', 10)
      .attr('text-anchor', 'middle');

    const leaf = svg
      .selectAll('a')
      .data(root.leaves())
      .join('a')
      .attr('transform', (d) => `translate(${d.x},${d.y})`);

    leaf
      .append('circle')
      .attr('stroke', props.isLightsOn ? 'red' : 'grey')
      .attr('stroke-width', 0.5)
      .attr('stroke-opacity', 0.8)
      .attr('fill', props.isLightsOn ? randomFillColor : randomFillColorGrey)
      .attr('fill-opacity', 0.6)
      .attr('r', (d) => d.r);

    leaf
      .append('text')
      .selectAll('tspan')
      // @ts-ignore
      .data((d) => `${scales.labels[d.data]}`.split(/\n/g))
      .join('tspan')
      .attr('x', 0)
      // eslint-disable-next-line @typescript-eslint/no-shadow
      .attr('y', (_d, i, D) => `${i - D.length / 2 + 0.85}em`)
      // eslint-disable-next-line @typescript-eslint/no-shadow
      .attr('fill-opacity', (_d, i, D) => (i === D.length - 1 ? 0.7 : null))
      .text((d) => d);
  }, [props.data, props.dimensions, props.isLightsOn]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      memoizedDrawCallback();
      memoizedUpdateCallback();
    }
  }, [loaded, memoizedDrawCallback, memoizedUpdateCallback]);

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
      memoizedDrawCallback();
      memoizedUpdateCallback();
    }
  }, [loaded, memoizedDrawCallback, memoizedUpdateCallback]);

  useEffect(() => {
    const isNewHeight = prevHeight !== props.dimensions.height;
    const isNewWidth = prevWidth !== props.dimensions.width;
    if (isNewHeight || isNewWidth) {
      // console.log(`update dimensions`);
      setPrevWidth(props.dimensions.height);
      setPrevHeight(props.dimensions.width);
      memoizedDrawCallback();
      memoizedUpdateCallback();
    }
  }, [
    memoizedDrawCallback,
    memoizedUpdateCallback,
    prevHeight,
    prevWidth,
    props.dimensions.height,
    props.dimensions.width,
  ]);

  useEffect(() => {
    memoizedDrawCallback();
    memoizedUpdateCallback();
  }, [props.isLightsOn]);

  const wordClickHandler = () => {
    memoizedDrawCallback();
    memoizedUpdateCallback();
  };

  return (
    <>
      <div className="chart" style={{ cursor: 'pointer' }} onClick={wordClickHandler}>
        <svg
          id="bubblesWrapper"
          width={`${props.dimensions.width}px`}
          height={`${props.dimensions.height}px`}
        >
          <g
            id="bubbleBounds"
            style={{
              transform: `translate(${props.dimensions.margin.left}px, ${props.dimensions.margin.top}px)`,
            }}
          >
            <g id="word-cloud-chart-group" />
          </g>
        </svg>
      </div>
    </>
  );
};

interface IBubbleChartProps {
  dimensions: Types.Dimensions;
  data: Types.Data[];
  propertiesNames: string[];
  wordsPadding: number;
  isLightsOn: boolean;
}

export default BubbleChart;
